import { nbaVisualFixtures } from "./nba-visual-fixtures.ts";
import { requireScenarioAsset } from "./scenarios.ts";
const SUMMER_ID = "1522600045";
const FINALS_ID = "0042500404";
const REGULAR_ID = "0022500641";
function fromScenario(id, gameId, assetId, presentation) {
    const source = requireScenarioAsset(gameId, assetId);
    return {
        ...source,
        id,
        revealAtMs: 0,
        presentation: {
            ...source.presentation,
            ...presentation
        }
    };
}
function mediaFree(id, gameId, assetId, presentation) {
    return fromScenario(id, gameId, assetId, {
        image: undefined,
        imageAlt: undefined,
        video: undefined,
        videoPoster: undefined,
        mediaShape: undefined,
        caption: undefined,
        gallery: undefined,
        galleryCredit: undefined,
        source: undefined,
        storyteller: undefined,
        visual: undefined,
        actionLabel: undefined,
        durationLabel: undefined,
        ...presentation
    });
}
function scoreBoundary(id, gameLabel, periodLabel, scoreLabel, headline, body, accent) {
    return {
        id,
        gameLabel,
        annotation: {
            component: "Score boundary",
            source: "NBA scoreboard and play-by-play",
            trigger: `${periodLabel} buzzer`
        },
        asset: {
            id: `catalog-${id}`,
            revealAtMs: 0,
            kind: "insight",
            evidenceIds: [`score-${id}`],
            presentation: {
                label: periodLabel,
                eyebrow: `${periodLabel} · ${scoreLabel}`,
                headline,
                body,
                accent
            }
        }
    };
}
function finalScore(id, gameLabel, eyebrow, headline, body, accent, stats) {
    return {
        id,
        gameLabel,
        annotation: {
            component: "Final score",
            source: "NBA final box score",
            trigger: "Final buzzer"
        },
        asset: {
            id: `catalog-${id}`,
            revealAtMs: 0,
            kind: "recap",
            evidenceIds: [`final-${id}`],
            presentation: {
                label: "Final",
                eyebrow,
                headline,
                body,
                accent,
                stats
            }
        }
    };
}
function visualAsset(id, kind, label, eyebrow, headline, body, accent, visual) {
    return {
        id,
        revealAtMs: 0,
        kind,
        evidenceIds: visual.sourceRefs,
        presentation: {
            label,
            eyebrow,
            headline,
            body,
            accent,
            visual
        }
    };
}
export const feedItemSections = [
    {
        id: "game-lifecycle",
        eyebrow: "Game lifecycle",
        title: "Give every phase of the night a purpose",
        introduction: "The feed can set the scene, mark the score and close the story without waiting for a highlight.",
        families: [
            {
                kind: "cards",
                id: "FI-01",
                title: "Pregame scene-setter",
                description: "A strong opening image and the people who matter put the fan inside the night before the ball goes up.",
                examples: [
                    {
                        id: "pregame-scene",
                        gameLabel: "Clippers at Lakers · Summer League",
                        annotation: {
                            component: "Pregame hero",
                            source: "Getty photography and NBA game context",
                            trigger: "Before game"
                        },
                        asset: fromScenario("catalog-pregame-scene", SUMMER_ID, "game-open")
                    }
                ]
            },
            {
                kind: "cards",
                id: "FI-02",
                title: "Game-day essentials",
                description: "Venue, broadcast and player availability become one useful pregame briefing.",
                examples: [
                    {
                        id: "game-day-essentials",
                        gameLabel: "Pacers at Thunder · Regular season",
                        annotation: {
                            component: "Game-day briefing",
                            source: "NBA game, roster and alerts",
                            trigger: "Pregame information confirmed"
                        },
                        asset: {
                            id: "catalog-game-day-essentials",
                            revealAtMs: 0,
                            kind: "welcome",
                            evidenceIds: [
                                "nba-game-paycom-center",
                                "nba-broadcast-fdsnok-fdsnin-siriusxm",
                                "nba-status-haliburton-mathurin"
                            ],
                            presentation: {
                                label: "Tonight's game",
                                eyebrow: "Paycom Center · 8:00 PM ET",
                                headline: "Everything you need before the rematch",
                                body: "Watch on FDSNOK or FDSNIN and listen on SiriusXM. Tyrese Haliburton and Bennedict Mathurin are inactive through injury.",
                                accent: "#FDBB30",
                                stats: [
                                    { value: "OKC", label: "VENUE" },
                                    { value: "2", label: "TV FEEDS" },
                                    { value: "2", label: "INACTIVE" }
                                ]
                            }
                        }
                    }
                ]
            },
            {
                kind: "cards",
                id: "FI-03",
                title: "Opening tip",
                description: "A clean live marker establishes the clock, the score and the start of the game story.",
                examples: [
                    {
                        id: "summer-tip",
                        gameLabel: "Clippers at Lakers · Summer League",
                        annotation: {
                            component: "Live marker",
                            source: "NBA scoreboard",
                            trigger: "Opening tip"
                        },
                        asset: mediaFree("catalog-summer-tip", SUMMER_ID, "tip-off-live", {
                            eyebrow: "1st · 10:00 · 0–0",
                            body: "The ball is up in Las Vegas. Clippers 0, Lakers 0—and the live story starts here."
                        })
                    },
                    {
                        id: "finals-tip",
                        gameLabel: "Spurs at Knicks · NBA Finals",
                        annotation: {
                            component: "Live marker",
                            source: "NBA scoreboard",
                            trigger: "Opening tip"
                        },
                        asset: mediaFree("catalog-finals-tip", FINALS_ID, "finals-tip-live", {
                            eyebrow: "1st · 12:00 · 0–0",
                            body: "The ball is up at Madison Square Garden. Spurs 0, Knicks 0—and every possession carries Finals weight."
                        })
                    },
                    {
                        id: "regular-tip",
                        gameLabel: "Pacers at Thunder · Regular season",
                        annotation: {
                            component: "Live marker",
                            source: "NBA scoreboard",
                            trigger: "Opening tip"
                        },
                        asset: mediaFree("catalog-regular-tip", REGULAR_ID, "rematch-tip-live", {
                            eyebrow: "1st · 12:00 · 0–0",
                            body: "Indiana and Oklahoma City are underway at Paycom Center. Pacers 0, Thunder 0."
                        })
                    }
                ]
            },
            {
                kind: "cards",
                id: "FI-04",
                title: "Quarter score boundaries",
                description: "Every buzzer gets a concise score receipt, with language shaped by the game rather than a generic template.",
                examples: [
                    scoreBoundary("summer-q1", "Clippers at Lakers · Summer League", "End of 1st", "Lakers 24–16", "The Lakers own the early edge", "Four made threes create an eight-point cushion after ten minutes.", "#FDB927"),
                    scoreBoundary("summer-q2", "Clippers at Lakers · Summer League", "End of 2nd", "Lakers 54–38", "Los Angeles finds another gear", "A 30-point quarter turns control into a 16-point halftime lead.", "#FDB927"),
                    scoreBoundary("summer-q3", "Clippers at Lakers · Summer League", "End of 3rd", "Lakers 83–60", "Challenge met. Door slammed.", "The Clippers got within six before the Lakers answered with the defining run of the night.", "#FDB927"),
                    scoreBoundary("summer-q4", "Clippers at Lakers · Summer League", "End of 4th", "Lakers 99–85", "The Lakers finish unbeaten in Vegas", "A balanced closing quarter completes a third Summer League win.", "#FDB927"),
                    scoreBoundary("finals-q1", "Spurs at Knicks · NBA Finals", "End of 1st", "Spurs 41–22", "San Antonio silences the Garden", "Forty-one points and a 19-point lead put the opening quarter entirely on the Spurs' terms.", "#C4CED4"),
                    scoreBoundary("finals-q2", "Spurs at Knicks · NBA Finals", "End of 2nd", "Spurs 76–49", "A shooting clinic cracks Game 4 open", "Eleven first-half threes send San Antonio to the break with a 27-point lead.", "#C4CED4"),
                    scoreBoundary("finals-q3", "Spurs at Knicks · NBA Finals", "End of 3rd", "Spurs 90–75", "The Garden believes again", "New York removes 14 points from the deficit and gives the final quarter a pulse.", "#F58426"),
                    scoreBoundary("finals-q4", "Spurs at Knicks · NBA Finals", "End of 4th", "Knicks 107–106", "New York completes the impossible", "The Knicks erase 29 points and win Game 4 by one at the final buzzer.", "#F58426"),
                    scoreBoundary("regular-q1", "Pacers at Thunder · Regular season", "End of 1st", "Pacers 39–28", "Indiana lands the first punch", "Thirty-nine points force Oklahoma City into chase mode early.", "#FDBB30"),
                    scoreBoundary("regular-q2", "Pacers at Thunder · Regular season", "End of 2nd", "Pacers 58–53", "Indiana bends, but keeps the lead", "A 17-point cushion is down to five as the rematch reaches halftime.", "#FDBB30"),
                    scoreBoundary("regular-q3", "Pacers at Thunder · Regular season", "End of 3rd", "Pacers 89–85", "The Thunder are at the door", "Indiana still leads, but twelve minutes remain in a one-possession game.", "#F05133"),
                    scoreBoundary("regular-q4", "Pacers at Thunder · Regular season", "End of 4th", "Pacers 117–114", "Indiana survives the final surge", "The Pacers withstand 47 from Gilgeous-Alexander and close a three-point win.", "#FDBB30")
                ]
            },
            {
                kind: "cards",
                id: "FI-05",
                title: "Halftime story",
                description: "The break becomes a designed catch-up moment: score, central character and the shape of the first half.",
                examples: [
                    {
                        id: "summer-halftime",
                        gameLabel: "Clippers at Lakers · Summer League",
                        annotation: {
                            component: "Halftime catch-up",
                            source: "NBA box score and photography",
                            trigger: "Halftime"
                        },
                        asset: fromScenario("catalog-summer-halftime", SUMMER_ID, "halftime-catch-up")
                    },
                    {
                        id: "finals-halftime",
                        gameLabel: "Spurs at Knicks · NBA Finals",
                        annotation: {
                            component: "Halftime catch-up",
                            source: "NBA box score",
                            trigger: "Halftime"
                        },
                        asset: mediaFree("catalog-finals-halftime", FINALS_ID, "finals-halftime")
                    },
                    {
                        id: "regular-halftime",
                        gameLabel: "Pacers at Thunder · Regular season",
                        annotation: {
                            component: "Halftime highlight",
                            source: "NBA score and game video",
                            trigger: "Halftime"
                        },
                        asset: fromScenario("catalog-regular-halftime", REGULAR_ID, "siakam-halftime")
                    }
                ]
            },
            {
                kind: "cards",
                id: "FI-06",
                title: "Final score and consequence",
                description: "The final card says what happened and what the result changes, without waiting for a long recap.",
                examples: [
                    finalScore("summer-final", "Clippers at Lakers · Summer League", "Lakers 99 · Clippers 85", "Carr powers a balanced Lakers win", "Los Angeles moves to 3–0 in Las Vegas behind 23 from Cameron Carr and 13 made threes.", "#FDB927", [
                        { value: "99–85", label: "FINAL" },
                        { value: "3–0", label: "VEGAS" },
                        { value: "23", label: "CARR" }
                    ]),
                    finalScore("finals-final", "Spurs at Knicks · NBA Finals", "Knicks 107 · Spurs 106", "A comeback for the history books", "New York erases 29 points, wins by one and takes a 3–1 Finals lead.", "#F58426", [
                        { value: "107–106", label: "FINAL" },
                        { value: "29", label: "COMEBACK" },
                        { value: "3–1", label: "SERIES" }
                    ]),
                    finalScore("regular-final", "Pacers at Thunder · Regular season", "Pacers 117 · Thunder 114", "Indiana withstands a 47-point storm", "Nembhard and Walker combine for 53 as the Pacers survive Oklahoma City's last possession.", "#FDBB30", [
                        { value: "117–114", label: "FINAL" },
                        { value: "27 / 11", label: "NEMBHARD" },
                        { value: "47", label: "SGA" }
                    ])
                ]
            }
        ]
    },
    {
        id: "on-court-intelligence",
        eyebrow: "On-court intelligence",
        title: "Explain why the game is changing",
        introduction: "Play-by-play, lineups, tracking and box-score evidence become feed-native stories a fan can understand at a glance.",
        families: [
            {
                kind: "cards",
                id: "FI-07",
                title: "Player scoring burst",
                description: "A timely player card combines the current total with the run or shot that made it matter.",
                examples: [
                    {
                        id: "carr-burst",
                        gameLabel: "Clippers at Lakers · Summer League",
                        annotation: {
                            component: "Player burst",
                            source: "NBA play-by-play and box score",
                            trigger: "Player reaches game-high 23"
                        },
                        asset: mediaFree("catalog-carr-burst", SUMMER_ID, "carr-final-three")
                    },
                    {
                        id: "nembhard-burst",
                        gameLabel: "Pacers at Thunder · Regular season",
                        annotation: {
                            component: "Player burst",
                            source: "NBA play-by-play and box score",
                            trigger: "Third made three"
                        },
                        asset: mediaFree("catalog-nembhard-burst", REGULAR_ID, "nembhard-hot-hand", {
                            stats: [
                                { value: "16", label: "PTS" },
                                { value: "3", label: "3PM" },
                                { value: "+17", label: "LEAD" }
                            ]
                        })
                    },
                    {
                        id: "sga-burst",
                        gameLabel: "Pacers at Thunder · Regular season",
                        annotation: {
                            component: "Player burst",
                            source: "NBA play-by-play and box score",
                            trigger: "One-point game"
                        },
                        asset: mediaFree("catalog-sga-burst", REGULAR_ID, "sga-one-point")
                    }
                ]
            },
            {
                kind: "cards",
                id: "FI-08",
                title: "Possession map",
                description: "The decisive sequence becomes a court story that preserves order, result and the winning touch.",
                examples: [
                    {
                        id: "winning-possession",
                        gameLabel: "Spurs at Knicks · NBA Finals",
                        annotation: {
                            component: "Court sequence",
                            source: "NBA play-by-play and shot locations",
                            trigger: "Game-winning possession"
                        },
                        asset: fromScenario("catalog-winning-possession", FINALS_ID, "finals-winning-court")
                    }
                ]
            },
            {
                kind: "cards",
                id: "FI-09",
                title: "Shot profile comparison",
                description: "Tracking data reveals how two leading guards created their points, without turning the feed into a dashboard.",
                examples: [
                    {
                        id: "pull-up-profile",
                        gameLabel: "Pacers at Thunder · Regular season",
                        annotation: {
                            component: "Shot profile",
                            source: "NBA tracking shots",
                            trigger: "Full-game player comparison"
                        },
                        asset: visualAsset("catalog-pull-up-profile", "insight", "Shot profile", "Full game · Pull-up shooting", "Nembhard finds the cleaner pull-up night", "The two lead guards created in different ways: Nembhard made five of eight pull-ups while Gilgeous-Alexander carried far more volume.", "#FDBB30", nbaVisualFixtures.regularPullUpProfile)
                    }
                ]
            },
            {
                kind: "cards",
                id: "FI-10",
                title: "Game-flow story",
                description: "The score line is edited into the handful of swings that define the night.",
                examples: [
                    {
                        id: "finals-flow",
                        gameLabel: "Spurs at Knicks · NBA Finals",
                        annotation: {
                            component: "Score-flow story",
                            source: "NBA official scoring timeline",
                            trigger: "Final score confirmed"
                        },
                        asset: fromScenario("catalog-finals-flow", FINALS_ID, "finals-comeback-flow")
                    },
                    {
                        id: "regular-flow",
                        gameLabel: "Pacers at Thunder · Regular season",
                        annotation: {
                            component: "Score-flow story",
                            source: "NBA official scoring timeline",
                            trigger: "Final score confirmed"
                        },
                        asset: visualAsset("catalog-regular-flow", "recap", "Game flow", "Final · Official scoring timeline", "Seventeen points became one possession", "Indiana's cushion disappeared, but the Pacers answered the final Thunder push and won by three.", "#FDBB30", nbaVisualFixtures.regularOnePossessionFlow)
                    }
                ]
            },
            {
                kind: "cards",
                id: "FI-11",
                title: "Lineup impact",
                description: "Five-player combinations become a simple receipt for the unit that changed or closed the game.",
                examples: [
                    {
                        id: "summer-lineup",
                        gameLabel: "Clippers at Lakers · Summer League",
                        annotation: {
                            component: "Lineup receipt",
                            source: "NBA lineup box score",
                            trigger: "Closing group completes its stint"
                        },
                        asset: fromScenario("catalog-summer-lineup", SUMMER_ID, "summer-closing-lineup")
                    },
                    {
                        id: "finals-lineup",
                        gameLabel: "Spurs at Knicks · NBA Finals",
                        annotation: {
                            component: "Lineup receipt",
                            source: "NBA lineup box score",
                            trigger: "Comeback unit completes its stint"
                        },
                        asset: fromScenario("catalog-finals-lineup", FINALS_ID, "finals-comeback-lineup")
                    }
                ]
            },
            {
                kind: "cards",
                id: "FI-12",
                title: "Invisible impact",
                description: "Hustle events reveal the off-ball action behind points that the standard box score misses.",
                examples: [
                    {
                        id: "robinson-impact",
                        gameLabel: "Spurs at Knicks · NBA Finals",
                        annotation: {
                            component: "Impact receipt",
                            source: "NBA hustle events",
                            trigger: "Third screen assist"
                        },
                        asset: fromScenario("catalog-robinson-impact", FINALS_ID, "finals-robinson-impact")
                    },
                    {
                        id: "potter-impact",
                        gameLabel: "Pacers at Thunder · Regular season",
                        annotation: {
                            component: "Impact receipt",
                            source: "NBA hustle events",
                            trigger: "Fifth screen assist"
                        },
                        asset: fromScenario("catalog-potter-impact", REGULAR_ID, "regular-potter-impact")
                    }
                ]
            },
            {
                kind: "cards",
                id: "FI-13",
                title: "NBA insight explained",
                description: "An NBA insight becomes a concise claim with the three facts a fan needs to trust it.",
                examples: [
                    {
                        id: "finals-api-insight",
                        gameLabel: "Spurs at Knicks · NBA Finals",
                        annotation: {
                            component: "Insight receipt",
                            source: "NBA insight, box score and play-by-play",
                            trigger: "Historic comeback confirmed"
                        },
                        asset: visualAsset("catalog-finals-api-insight", "insight", "By the numbers", "Final · Historic comeback", "The comeback in three numbers", "New York erased the largest deficit in NBA Finals history and finished one point ahead.", "#F58426", nbaVisualFixtures.finalsComebackApiInsight)
                    },
                    {
                        id: "regular-api-insight",
                        gameLabel: "Pacers at Thunder · Regular season",
                        annotation: {
                            component: "Insight receipt",
                            source: "NBA insight, box score and play-by-play",
                            trigger: "Final player totals confirmed"
                        },
                        asset: visualAsset("catalog-regular-api-insight", "insight", "By the numbers", "Final · Star performances", "Forty-seven was not enough", "Gilgeous-Alexander carried Oklahoma City, but Indiana found 53 points from Nembhard and Walker.", "#FDBB30", nbaVisualFixtures.regularSgaApiInsight)
                    }
                ]
            }
        ]
    },
    {
        id: "media-and-sources",
        eyebrow: "Media and sources",
        title: "Choose the strongest expression for each moment",
        introduction: "Photography, official social, vertical video and Stories can work as native feed items rather than disconnected links.",
        families: [
            {
                kind: "cards",
                id: "FI-14",
                title: "Getty plus live evidence",
                description: "Editorial photography gains immediate meaning when it is paired with the live stat or insight it best expresses.",
                examples: [
                    {
                        id: "carr-getty-stat",
                        gameLabel: "Clippers at Lakers · Summer League",
                        annotation: {
                            component: "Photo and stat",
                            source: "Getty photography and NBA box score",
                            trigger: "Player reaches game-high 23"
                        },
                        asset: fromScenario("catalog-carr-getty-stat", SUMMER_ID, "carr-final-three")
                    },
                    {
                        id: "wemby-getty-insight",
                        gameLabel: "Spurs at Knicks · NBA Finals",
                        annotation: {
                            component: "Photo and insight",
                            source: "Getty photography and NBA box score",
                            trigger: "Halftime score confirmed"
                        },
                        asset: fromScenario("catalog-wemby-getty-insight", FINALS_ID, "finals-halftime")
                    }
                ]
            },
            {
                kind: "cards",
                id: "FI-15",
                title: "Official social video",
                description: "A team post arrives in the feed with its original voice, playable media and the game context around it.",
                examples: [
                    {
                        id: "spurs-social-video",
                        gameLabel: "Spurs at Knicks · NBA Finals",
                        annotation: {
                            component: "Official social video",
                            source: "San Antonio Spurs on X",
                            trigger: "Official post published"
                        },
                        asset: fromScenario("catalog-spurs-social-video", FINALS_ID, "spurs-storm")
                    }
                ]
            },
            {
                kind: "cards",
                id: "FI-16",
                title: "Official social result",
                description: "The team's own final word is preserved while the card supplies the result and consequence.",
                examples: [
                    {
                        id: "lakers-social-result",
                        gameLabel: "Clippers at Lakers · Summer League",
                        annotation: {
                            component: "Official result post",
                            source: "Los Angeles Lakers on X",
                            trigger: "Official post published"
                        },
                        asset: fromScenario("catalog-lakers-social-result", SUMMER_ID, "summer-final-social")
                    },
                    {
                        id: "pacers-social-result",
                        gameLabel: "Pacers at Thunder · Regular season",
                        annotation: {
                            component: "Official result post",
                            source: "Indiana Pacers on X",
                            trigger: "Official post published"
                        },
                        asset: fromScenario("catalog-pacers-social-result", REGULAR_ID, "pacers-final-social")
                    }
                ]
            },
            {
                kind: "cards",
                id: "FI-17",
                title: "Storyteller Story",
                description: "A multi-frame vertical Story opens in place, giving the fan a deeper chapter without leaving the feed.",
                examples: [
                    {
                        id: "finals-pregame-story",
                        gameLabel: "Spurs at Knicks · NBA Finals",
                        annotation: {
                            component: "Vertical Story",
                            source: "Storyteller",
                            trigger: "Pregame Story published"
                        },
                        asset: {
                            id: "catalog-finals-pregame-story",
                            revealAtMs: 0,
                            kind: "welcome",
                            evidenceIds: ["story-finalsgame4-pregame"],
                            presentation: {
                                label: "Before tip",
                                eyebrow: "NBA Finals · 26 frames",
                                headline: "Spurs seek a series split in New York",
                                body: "Open the complete pregame Story for the matchup, the stakes and the people at the centre of Game 4.",
                                accent: "#F58426",
                                storyteller: {
                                    type: "story",
                                    storyId: "3b4a3764-9a54-ebcb-c4b0-3a21bf208372",
                                    poster: "https://storyteller-assets.nba.com/assets/studio-templates/3b4a3764-9a54-ebcb-c4b0-3a21bf208372/72180b31-571d-4eff-a3c8-fc3ed1f55132/8490cf5a-4847-bb89-ff78-3a21c3e15fbc/thumbnail_small.jpeg",
                                    pageCount: 26,
                                    actionLabel: "Get ready for Game 4",
                                    accessibleLabel: "Open the 26-frame Spurs versus Knicks pregame story"
                                }
                            }
                        }
                    },
                    {
                        id: "finals-halftime-story",
                        gameLabel: "Spurs at Knicks · NBA Finals",
                        annotation: {
                            component: "Vertical Story",
                            source: "Storyteller",
                            trigger: "Halftime Story published"
                        },
                        asset: {
                            id: "catalog-finals-halftime-story",
                            revealAtMs: 0,
                            kind: "catch-up",
                            evidenceIds: ["story-finalsgame4-halftime"],
                            presentation: {
                                label: "Halftime Story",
                                eyebrow: "Spurs 76 · Knicks 49 · 9 frames",
                                headline: "The Spurs light up the first half",
                                body: "Open the vertical halftime Story for the record-setting shooting and the scale of New York's challenge.",
                                accent: "#C4CED4",
                                storyteller: {
                                    type: "story",
                                    storyId: "4711a96a-e7d0-2150-3f8f-3a21c687bf34",
                                    poster: "https://storyteller-assets.nba.com/assets/studio-templates/4711a96a-e7d0-2150-3f8f-3a21c687bf34/a5d4616f-961c-4c82-8061-eb4010e171d4/000095fa-0164-48d9-8a89-3a21c6a5ae4e/thumbnail_small.jpeg",
                                    pageCount: 9,
                                    actionLabel: "See the first-half story",
                                    accessibleLabel: "Open the nine-frame Spurs versus Knicks halftime story"
                                }
                            }
                        }
                    },
                    {
                        id: "regular-final-story",
                        gameLabel: "Pacers at Thunder · Regular season",
                        annotation: {
                            component: "Vertical Story",
                            source: "Storyteller",
                            trigger: "Final Story published"
                        },
                        asset: fromScenario("catalog-regular-final-story", REGULAR_ID, "pacers-recap")
                    }
                ]
            },
            {
                kind: "cards",
                id: "FI-18",
                title: "Storyteller Clip",
                description: "A 9:16 highlight is immediately playable in the feed, with the event and timing already understood.",
                examples: [
                    {
                        id: "finals-winning-clip",
                        gameLabel: "Spurs at Knicks · NBA Finals",
                        annotation: {
                            component: "Vertical Clip",
                            source: "Storyteller",
                            trigger: "Game-winning moment available"
                        },
                        asset: fromScenario("catalog-finals-winning-clip", FINALS_ID, "anunoby-tip")
                    },
                    {
                        id: "summer-dunk-clip",
                        gameLabel: "Clippers at Lakers · Summer League",
                        annotation: {
                            component: "Vertical Clip",
                            source: "Storyteller",
                            trigger: "Dunk highlight available"
                        },
                        asset: fromScenario("catalog-summer-dunk-clip", SUMMER_ID, "thiero-rim-run")
                    },
                    {
                        id: "regular-three-clip",
                        gameLabel: "Pacers at Thunder · Regular season",
                        annotation: {
                            component: "Vertical Clip",
                            source: "Storyteller",
                            trigger: "Scoring highlight available"
                        },
                        asset: fromScenario("catalog-regular-three-clip", REGULAR_ID, "nembhard-hot-hand")
                    }
                ]
            },
            {
                kind: "cards",
                id: "FI-19",
                title: "End-of-game photo gallery",
                description: "A square, swipeable-feeling photo story closes the night with distinct frames rather than recycling one hero image.",
                examples: [
                    {
                        id: "summer-gallery",
                        gameLabel: "Clippers at Lakers · Summer League",
                        annotation: {
                            component: "Photo gallery",
                            source: "Getty photography",
                            trigger: "Final image set ready"
                        },
                        asset: fromScenario("catalog-summer-gallery", SUMMER_ID, "summer-photo-story")
                    },
                    {
                        id: "finals-gallery",
                        gameLabel: "Spurs at Knicks · NBA Finals",
                        annotation: {
                            component: "Photo gallery",
                            source: "Getty photography",
                            trigger: "Final image set ready"
                        },
                        asset: fromScenario("catalog-finals-gallery", FINALS_ID, "finals-photo-story")
                    },
                    {
                        id: "regular-gallery",
                        gameLabel: "Pacers at Thunder · Regular season",
                        annotation: {
                            component: "Photo gallery",
                            source: "Getty photography",
                            trigger: "Final image set ready"
                        },
                        asset: fromScenario("catalog-regular-gallery", REGULAR_ID, "regular-photo-story")
                    }
                ]
            }
        ]
    },
    {
        id: "content-rules",
        eyebrow: "Content rules",
        title: "Good judgement is part of the product",
        introduction: "The strongest feed is selective. It uses only supported evidence, publishes at the right moment and avoids repetition.",
        families: [
            {
                kind: "rules",
                id: "FI-20",
                title: "Editorial rules",
                description: "Clear rules keep the experience credible when a source is late, unavailable or already represented.",
                rules: [
                    {
                        title: "Use evidence that exists",
                        body: "Tracking, hustle and NBA insight formats are omitted for Summer League when those sources are unavailable."
                    },
                    {
                        title: "Publish in the moment",
                        body: "An asset that arrives too late is not inserted back into an earlier chapter of the game."
                    },
                    {
                        title: "Show each asset once",
                        body: "Repeated media, posts, Stories and Clips are rejected so every feed item earns its place."
                    },
                    {
                        title: "Omit unsupported formats",
                        body: "When a format cannot be made honestly, the feed stays clean instead of manufacturing an error card."
                    }
                ]
            }
        ]
    }
];
export const feedItemFamilies = feedItemSections.flatMap((section) => section.families);
